import { AsyncPipe, CurrencyPipe, DecimalPipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { createPopper } from '@popperjs/core';
import { BehaviorSubject, Observable, catchError, distinctUntilChanged, map, take, of, switchMap } from 'rxjs';
import { NGXLogger } from 'ngx-logger';
import { HierarchyNode } from '../../../models/hierarchy-node.model';
import { Organization } from '../../../models/organization.model';
import { v4 as uuidv4 } from 'uuid';
import { Group } from '../../../models/group.model';
import {
  MemberType,
  MembershipStatus,
} from '../../../models/membership.status';
import { InitialsPipe } from '../../../pipes/initials.pipe';
import { ManagementService } from '../../../services/management.service';
import { AppState } from '../../../state/appstate';
import * as groupSelector from '../../../state/selectors/group.selector';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import { BtnMemberStatusComponent } from '../../components/buttons/btn-member-status/btn-member-status.component';
import { BtnRouteComponent } from '../../components/buttons/btn-route/btn-route.component';
import { ViewLocationComponent } from '../../components/maps/view-location/view-location.component';
import { AccountingService } from '../../../services/accounting.service';
import { MatDialog } from '@angular/material/dialog';
import { DlgViewProfileComponent } from '../../profile/dlg-view-profile/dlg-view-profile.component';
import { DlgViewLocationComponent } from '../../components/maps/dlg-view-location/dlg-view-location.component';
import { BtnActionComponent } from '../../components/buttons/btn-action/btn-action.component';
import { BtnFabComponent } from '../../components/buttons/btn-fab/btn-fab.component';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    RouterLink,
    AsyncPipe,
    CurrencyPipe,
    DecimalPipe,
    BtnRouteComponent,
    BtnBackComponent,
    BtnMemberStatusComponent,
    BtnActionComponent,
    BtnFabComponent,
    InitialsPipe,
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
})
export class HomeComponent implements AfterViewInit {
  dropdownPopoverShow = false;
  @ViewChild('btnDropdownRef', { static: false }) btnDropdownRef!: ElementRef;
  @ViewChild('popoverDropdownRef', { static: false })
  popoverDropdownRef: ElementRef | undefined;

  router = inject(Router);
  route: ActivatedRoute = inject(ActivatedRoute);
  private accountingService = inject(AccountingService);
  private managementService = inject(ManagementService);
  private dialog = inject(MatDialog);
  private logger = inject(NGXLogger);

  uuid = uuidv4();

  group$: Observable<Group | null | undefined> = new Observable();

  groupId: string | null = null;
  location$: BehaviorSubject<Geolocation | undefined> = new BehaviorSubject<
    Geolocation | undefined
  >(undefined);

  myRoles$: Observable<any> = new Observable();
  activeFunding$: Observable<any> = new Observable();
  pendingAffiliationInvites$: Observable<number> = of(0);
  // The church's organization and the units above it, for the Organization card (members only)
  affiliation$: Observable<{ organization: Organization | null; lineage: HierarchyNode[] } | null> = of(null);

  membershipStatus = MembershipStatus;
  memberType = MemberType;

  constructor(private store: Store<AppState>) {
    this.route.paramMap.subscribe(params => {
      this.groupId = params.get('id');
    });

    const groupId$ = this.route.paramMap.pipe(
      map(params => params.get('id'))
    );

    this.group$ = groupId$.pipe(
      switchMap(id => this.store.select(groupSelector.getGroupById(id)))
    );

    this.myRoles$ = groupId$.pipe(
      switchMap(id => this.store.select(groupSelector.getMyRolesInGroup(id)))
    );

    // Organizations inviting this church to join their org chart (acted on by church admins)
    this.pendingAffiliationInvites$ = groupId$.pipe(
      switchMap((id) =>
        id
          ? this.managementService
              .getChurchAffiliationInvites(id)
              .pipe(map((list) => list.filter((i) => i.direction !== 'request').length))
          : of(0)
      )
    );

    // Loaded once per church/org (the callable checks the user is an active member of the church)
    this.affiliation$ = this.group$.pipe(
      map((g) =>
        g?.organizationId && g.memberStatus === MembershipStatus.active ? `${g.id}|${g.organizationId}` : null
      ),
      distinctUntilChanged(),
      switchMap((key) => {
        if (!key) return of(null);
        const churchId = key.split('|')[0];
        return this.managementService.getChurchOversight(churchId).pipe(
          map(({ organization, lineage }) => ({ organization, lineage })),
          catchError((err) => {
            this.logger.error('home.component.ts :: Loading church affiliation failed:', { churchId, err });
            return of(null);
          })
        );
      })
    );

    this.activeFunding$ = groupId$.pipe(
      switchMap(id => {
        if (id) {
          return this.accountingService.getFunding(id).pipe(
            map((fundingList) => fundingList.find((f) => f.status === 'active') || null)
          );
        } else {
          return of(null);
        }
      })
    );
  }

  ngAfterViewInit() {
    createPopper(
      this.btnDropdownRef?.nativeElement,
      this.popoverDropdownRef?.nativeElement,
      {
        placement: 'bottom-start',
      },
    );
  }

  toggleDropdown(event: any) {
    event.preventDefault();
    event.stopPropagation();
    this.dropdownPopoverShow = !this.dropdownPopoverShow;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    this.dropdownPopoverShow = false;
  }

  onViewProfile() {
    this.dialog.open(DlgViewProfileComponent);
  }

  openMap() {
    this.group$.pipe(take(1)).subscribe((group) => {
      if (group) {
        this.dialog.open(DlgViewLocationComponent, {
          data: {
            location: group.location
          }
        });
      }
    });
  }
}
